/**
 * Audit Log Dashboard - View history of all CRM changes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  History, 
  RefreshCw, 
  Search,
  User,
  Package,
  ShoppingCart,
  Users,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_ICONS = {
  order: ShoppingCart,
  customer: Users,
  product: Package,
  user: User,
};

const ENTITY_COLORS = {
  order: 'bg-blue-100 text-blue-700',
  customer: 'bg-green-100 text-green-700',
  product: 'bg-purple-100 text-purple-700',
  user: 'bg-orange-100 text-orange-700',
};

const ACTION_COLORS = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
  status_changed: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
};

const AuditLogDashboard = ({ token, language = 'es' }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  
  // Filters
  const [days, setDays] = useState(7);
  const [entityType, setEntityType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        days: days.toString(),
        page: page.toString(),
        limit: '30',
      });
      if (entityType) params.append('entity_type', entityType);
      if (searchTerm) params.append('search', searchTerm);

      const [logsRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/audit?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/audit/summary?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData.logs || []);
        setPagination(logsData.pagination || { page: 1, total: 0, total_pages: 1 });
      }

      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [token, days, entityType, searchTerm]);

  useEffect(() => {
    fetchLogs(1);
  }, [days, entityType]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action) => {
    if (action.includes('created')) return ACTION_COLORS.created;
    if (action.includes('updated')) return ACTION_COLORS.updated;
    if (action.includes('deleted')) return ACTION_COLORS.deleted;
    if (action.includes('status')) return ACTION_COLORS.status_changed;
    if (action.includes('approved')) return ACTION_COLORS.approved;
    return 'bg-gray-100 text-gray-700';
  };

  const EntityIcon = ({ type }) => {
    const Icon = ENTITY_ICONS[type] || Package;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <History className="h-6 w-6" />
          {language === 'es' ? 'Historial de Auditoría' : 'Audit History'}
        </h2>
        <Button onClick={() => fetchLogs(pagination.page)} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {language === 'es' ? 'Actualizar' : 'Refresh'}
        </Button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">{language === 'es' ? 'Total Eventos' : 'Total Events'}</p>
              <p className="text-2xl font-bold">{summary.total_events}</p>
            </CardContent>
          </Card>
          {summary.by_entity?.slice(0, 3).map((entity, idx) => (
            <Card key={idx}>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground capitalize">{entity.type}</p>
                <p className="text-2xl font-bold">{entity.count}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Activity Chart */}
      {summary?.daily_activity && summary.daily_activity.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {language === 'es' ? 'Actividad por Día' : 'Daily Activity'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={summary.daily_activity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(val) => val?.slice(5) || val}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={language === 'es' ? 'Buscar...' : 'Search...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={entityType || "all"} onValueChange={(v) => setEntityType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder={language === 'es' ? 'Tipo' : 'Type'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
              <SelectItem value="order">{language === 'es' ? 'Pedidos' : 'Orders'}</SelectItem>
              <SelectItem value="customer">{language === 'es' ? 'Clientes' : 'Customers'}</SelectItem>
              <SelectItem value="product">{language === 'es' ? 'Productos' : 'Products'}</SelectItem>
              <SelectItem value="user">{language === 'es' ? 'Usuarios' : 'Users'}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 {language === 'es' ? 'día' : 'day'}</SelectItem>
              <SelectItem value="7">7 {language === 'es' ? 'días' : 'days'}</SelectItem>
              <SelectItem value="14">14 {language === 'es' ? 'días' : 'days'}</SelectItem>
              <SelectItem value="30">30 {language === 'es' ? 'días' : 'days'}</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit">
            <Filter className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Filtrar' : 'Filter'}
          </Button>
        </form>
      </Card>

      {/* Audit Logs List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {language === 'es' ? 'Registro de Cambios' : 'Change Log'}
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({pagination.total} {language === 'es' ? 'eventos' : 'events'})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {language === 'es' ? 'No hay eventos registrados' : 'No events recorded'}
            </p>
          ) : (
            <div className="space-y-3">
              {logs.map((log, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className={`p-2 rounded-full ${ENTITY_COLORS[log.entity_type] || 'bg-gray-100'}`}>
                    <EntityIcon type={log.entity_type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={getActionColor(log.action)}>
                        {log.action_label || log.action}
                      </Badge>
                      <span className="font-medium truncate">{log.entity_name || log.entity_id}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span>{log.user_email || 'Sistema'}</span>
                      <span>•</span>
                      <span>{formatDate(log.timestamp)}</span>
                    </div>
                    {(log.old_values || log.new_values) && (
                      <div className="mt-2 text-xs bg-white p-2 rounded border">
                        {log.old_values && Object.keys(log.old_values).length > 0 && (
                          <div className="text-red-600">
                            <strong>Antes:</strong> {JSON.stringify(log.old_values)}
                          </div>
                        )}
                        {log.new_values && Object.keys(log.new_values).length > 0 && (
                          <div className="text-green-600">
                            <strong>Después:</strong> {JSON.stringify(log.new_values)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                {pagination.page} / {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogDashboard;
